import React, { PropsWithChildren } from 'react';
import clsx from 'clsx';
import { Box } from '@mui/material';
import { grey } from '@mui/material/colors';
import { Theme } from '@mui/material/styles';
import { useDrag, useDrop } from 'react-dnd';
import makeStyles from '@mui/styles/makeStyles';

import { BoardCardProps } from './types';
import { DND_CARD_TYPE } from './constants';

export const BoardCard = <T,>({
  id,
  index,
  columnId,
  children,
  onMoveCard,
  metaData,
}: PropsWithChildren<BoardCardProps<T>>) => {
  const isCardSupportDAD = metaData.isSupportDAD;

  const classes = useStyles({ isSupportDAD: isCardSupportDAD });

  const [{ isDragging }, drag] = useDrag({
    type: DND_CARD_TYPE,
    item: { id, index, columnId },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: DND_CARD_TYPE,
    hover: (dragItem: any) => {
      if (dragItem.id !== id && dragItem.columnId === columnId) {
        onMoveCard({
          toIndex: index,
          toColumnId: columnId,
          fromColumnId: columnId,
          itemIndex: dragItem.index,
        });

        dragItem.index = index;
      }
    },
  });

  return (
    <Box
      className={clsx(classes.boardCard, { [classes.dragging]: isDragging })}
      ref={isCardSupportDAD ? node => drag(drop(node as any)) : null}
    >
      {children}
    </Box>
  );
};

const useStyles = makeStyles<Theme, { isSupportDAD: boolean }>(({ shadows }) => ({
  boardCard: {
    padding: '0.5rem',
    borderRadius: '0.5rem',
    backgroundColor: grey[100],
    cursor: ({ isSupportDAD }) => (isSupportDAD ? 'grab' : 'default'),

    '&:hover': {
      boxShadow: shadows[1],
    },
  },
  dragging: {
    backgroundColor: grey[300],
  },
}));
